import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { del, get } from "@/api/client";
import CopyButton from "@/components/CopyButton";
import { useToast } from "@/components/Toast";
import { MONO } from "@/portal/helpers";

interface Connection {
  id: string;
  client_name: string;
  created_at: string;
  last_used_at: string | null;
}

const when = (value: string | null) => (value ? new Date(value).toLocaleString() : "never");

/** Your account → Connected AI apps: how to connect Claude, and what is connected. */
export default function ConnectedAppsCard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const url = `${window.location.origin}/api/mcp`;
  const connections = useQuery({
    queryKey: ["mcp-connections"],
    queryFn: () => get<{ connections: Connection[] }>("/mcp/connections"),
  });
  const disconnect = useMutation({
    mutationFn: (id: string) => del(`/mcp/connections/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["mcp-connections"] });
      toast.notify("Disconnected");
    },
    onError: (error) => toast.failed(error),
  });
  const claudeCode = `claude mcp add --transport http multidoc ${url}`;
  const rows = connections.data?.connections ?? [];

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" fontWeight={600}>Connected AI apps</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Let Claude (Desktop, claude.ai, Claude Code) or another MCP app work in the Console as
        you: look things up, generate proposals, upload files to deals and customers' portals.
        It can do only what you can, and every change is logged under your name.
      </Typography>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="body2" fontWeight={600}>Server address</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box component="code" sx={{ fontFamily: MONO, fontSize: 13 }}>{url}</Box>
            <CopyButton value={url} label="Copy" />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Claude Desktop / claude.ai: Settings → Connectors → Add custom connector, paste this
            address, then sign in here when asked.
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" fontWeight={600}>Claude Code</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box component="code" sx={{ fontFamily: MONO, fontSize: 13, wordBreak: "break-all" }}>
              {claudeCode}
            </Box>
            <CopyButton value={claudeCode} label="Copy" />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Then run /mcp in Claude Code to sign in. opencode and scripts can instead send a
            Console API key (Admin → API keys) as <code>Authorization: Bearer mdc_…</code>.
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Connected now</Typography>
          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {connections.isLoading ? "Loading…" : "No apps are connected."}
            </Typography>
          ) : rows.map((row) => (
            <Stack key={row.id} direction="row" alignItems="center" justifyContent="space-between"
                   sx={{ py: 0.5 }}>
              <Box>
                <Typography variant="body2">{row.client_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Connected {when(row.created_at)} · last used {when(row.last_used_at)}
                </Typography>
              </Box>
              <Button size="small" color="error" disabled={disconnect.isPending}
                      onClick={() => disconnect.mutate(row.id)}>
                Disconnect
              </Button>
            </Stack>
          ))}
        </Box>
      </Stack>
    </Paper>
  );
}
